import type { Group, Pouch } from '../../types';
import { syncGroupMembershipFromPouchPositions } from '../../logic/GroupLogic';
import { generateRoundedRectPath, snapToGrid } from '../../logic/GroupLogic';
import {
  placeNewPouches,
  repositionPouchesRemovedFromGroups,
  reconcileGroupsToFitMembers,
  enrollMovedPouchesForReconcile,
  computeSplitGroupInsets,
  computeSplitGroupBounds,
  computeBrickLayout,
  computeClusterBounds,
  replicaSubtotalBottomPadPx,
} from '../../logic/PouchPlacementLogic';

function needsAutoPlacement(
  pouchId: string,
  autoPlacePouchIds: Set<string>,
): boolean {
  return autoPlacePouchIds.has(pouchId);
}

/**
 * 自動配置対象 ID。
 * - x/y 省略で明示された ID
 * - 前回シートに無い新規パウチ（GTP 除く）— AI が座標を推測しても分解と同じ brick 配置に寄せる
 */
export function collectAutoPlacePouchIds(
  previousPouches: Pouch[],
  pouches: Pouch[],
  explicitAutoPlaceIds?: string[],
): string[] {
  const previousIds = new Set(previousPouches.map(p => p.id));
  const forcedNewIds = pouches
    .filter(p => !previousIds.has(p.id) && !p.isGroupTotalPouch)
    .map(p => p.id);
  return Array.from(
    new Set([...(explicitAutoPlaceIds ?? []), ...forcedNewIds]),
  );
}

/**
 * Bridge / CLI write 後の配置統合。
 * グループ外し再配置 → placeNewPouches（新規パウチ / 位置未定パウチ）。
 */
export function applyBridgePlacement(
  previousGroups: Group[],
  previousPouches: Pouch[],
  pouches: Pouch[],
  groups: Group[],
  autoPlacePouchIds?: string[],
): { pouches: Pouch[]; groups: Group[] } {
  const afterUngroup = repositionPouchesRemovedFromGroups(
    previousGroups,
    pouches,
    groups,
  );

  const autoPlaceSet = new Set(
    collectAutoPlacePouchIds(previousPouches, pouches, autoPlacePouchIds),
  );
  const shouldPlace = (id: string) => needsAutoPlacement(id, autoPlaceSet);

  let resultPouches = afterUngroup.map(p => ({ ...p }));
  let resultGroups = groups.map(g => ({ ...g }));

  const placedIds = new Set<string>();
  /** 同一 API write で配置済みの新規グループ（path 確定済み） */
  const placedGroupIds = new Set<string>();
  /** 同一 API write で並べる新規グループのパウチ行 Y（1行目基準） */
  let batchRowPouchY: number | null = null;

  const ungroupedToPlace = resultPouches.filter(
    p =>
      shouldPlace(p.id) &&
      !placedIds.has(p.id) &&
      !resultGroups.some(g => (g.pouches ?? []).includes(p.id)),
  );

  if (ungroupedToPlace.length > 0) {
    const contextPouches = resultPouches.filter(
      p => !ungroupedToPlace.some(u => u.id === p.id),
    );
    const plan = placeNewPouches({
      count: ungroupedToPlace.length,
      pouches: contextPouches,
      groups: resultGroups,
      mode: 'global',
      pouchTitles: ungroupedToPlace.map(p => p.title),
    });
    if (plan) {
      ungroupedToPlace.forEach((pouch, index) => {
        const pos = plan.positions[index];
        const target = resultPouches.find(p => p.id === pouch.id);
        if (target && pos) {
          target.x = pos.x;
          target.y = pos.y;
          placedIds.add(pouch.id);
        }
      });
    }
  }

  for (const group of resultGroups) {
    const memberIds = group.pouches ?? [];
    const toPlaceIds = memberIds.filter(
      id => shouldPlace(id) && !placedIds.has(id),
    );
    if (!toPlaceIds.length) continue;

    const isNewGroup = !previousGroups.some(g => g.id === group.id);
    const allMembersNeedPlace = toPlaceIds.length === memberIds.length;
    const fixedMemberIds = memberIds.filter(id => !toPlaceIds.includes(id));

    const contextPouches = resultPouches.filter(p => {
      if (toPlaceIds.includes(p.id)) return false;
      // 同バッチで未配置のパウチ (0,0) は障害物にしない
      if (shouldPlace(p.id) && !placedIds.has(p.id)) return false;
      return true;
    });
    const previousGroupIds = new Set(previousGroups.map(g => g.id));
    const contextGroups = isNewGroup && allMembersNeedPlace
      ? resultGroups.filter(
          g =>
            g.id !== group.id &&
            (previousGroupIds.has(g.id) || placedGroupIds.has(g.id)),
        )
      : resultGroups;

    if (isNewGroup && allMembersNeedPlace) {
      const memberPouches = memberIds.map(
        id => resultPouches.find(p => p.id === id)!,
      );
      const plan = placeNewPouches({
        count: memberIds.length,
        pouches: contextPouches,
        groups: contextGroups,
        mode: 'global',
        wrapWithGroup: true,
        groupTitle: group.title,
        pouchTitles: memberPouches.map(p => p.title),
        memberMultipliers: memberPouches.map(p => p.multiplier ?? 1),
      });
      if (!plan?.groupBounds) continue;

      let positions = memberIds.map(
        (_id, index) => plan.positions[index]!,
      );
      if (batchRowPouchY === null) {
        batchRowPouchY = positions[0]?.y ?? null;
      } else if (batchRowPouchY !== null && positions.length > 0) {
        const dy = batchRowPouchY - positions[0].y;
        if (dy !== 0) {
          positions = positions.map(pos => ({
            x: pos.x,
            y: snapToGrid(pos.y + dy),
          }));
        }
      }

      const titleForInsets = group.title?.trim() || '';
      const layoutAtOrigin = computeBrickLayout(
        memberIds.length,
        0,
        0,
        'topleft',
        { pouchTitles: memberPouches.map(p => p.title) },
      );
      let clusterMemberWidth =
        computeClusterBounds(layoutAtOrigin, 0, 0).right -
        computeClusterBounds(layoutAtOrigin, 0, 0).left;
      if (memberIds.length < 2) {
        const refLayout = computeBrickLayout(2, 0, 0, 'topleft');
        const refWidth =
          computeClusterBounds(refLayout, 0, 0).right -
          computeClusterBounds(refLayout, 0, 0).left;
        clusterMemberWidth = Math.max(clusterMemberWidth, refWidth);
      }
      const displayInsets = computeSplitGroupInsets(
        !!titleForInsets,
        titleForInsets,
        clusterMemberWidth,
        replicaSubtotalBottomPadPx(
          memberPouches.map(p => p.multiplier ?? 1),
        ),
      );
      const groupBounds = computeSplitGroupBounds(positions, displayInsets);

      const positionById = new Map(
        memberIds.map((id, index) => [id, positions[index]] as const),
      );
      resultPouches = resultPouches.map(p => {
        const pos = positionById.get(p.id);
        return pos ? { ...p, x: pos.x, y: pos.y } : p;
      });
      memberIds.forEach(id => placedIds.add(id));

      const path = generateRoundedRectPath(
        groupBounds.left,
        groupBounds.top,
        groupBounds.right,
        groupBounds.bottom,
      );
      resultGroups = resultGroups.map(g =>
        g.id === group.id ? { ...g, path } : g,
      );
      placedGroupIds.add(group.id);
      continue;
    }

    const plan = placeNewPouches({
      count: toPlaceIds.length,
      pouches: contextPouches,
      groups: contextGroups,
      mode: 'group',
      groupId: group.id,
      fixedMemberIds,
      pouchTitles: toPlaceIds.map(
        id => resultPouches.find(p => p.id === id)?.title,
      ),
    });
    if (!plan) continue;

    const positionById = new Map(
      toPlaceIds.map((id, index) => [id, plan.positions[index]] as const),
    );
    resultPouches = resultPouches.map(p => {
      const pos = positionById.get(p.id);
      return pos ? { ...p, x: pos.x, y: pos.y } : p;
    });
    toPlaceIds.forEach(id => placedIds.add(id));
    resultPouches = plan.pouches;
    resultGroups = plan.groups;
  }

  const groupsForReconcile = enrollMovedPouchesForReconcile(
    previousPouches,
    previousGroups,
    resultPouches,
    resultGroups,
  );
  const fitted = reconcileGroupsToFitMembers(
    previousPouches,
    resultPouches,
    groupsForReconcile,
    previousGroups,
  );
  const syncedGroups = syncGroupMembershipFromPouchPositions(
    fitted.pouches,
    fitted.groups,
  );
  return { pouches: fitted.pouches, groups: syncedGroups };
}
